import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { RoutePreviewStop } from "@mapmycustomers/shared/types/entity/Route";

import { EditorRouteStop } from "../types/RouteStop";

// Placeholder preview metrics for synthetic stops built without a preview. The stylers read only
// entity/id, geoPoint, name, displayOrder and scheduledTime(-Skipped) off each stop; the numeric
// RoutePreviewDetails fields are never read in that state, so zero/"" is safe.
export const PREVIEW_METRIC_DEFAULTS = {
  arrivalTime: "",
  originalIndex: 0,
  travelDistance: 0,
  travelTime: 0,
};

/**
 * Project a route stop into the `RoutePreviewStop` shape the map's stop markers consume.
 *
 * The map draws its stops from a preview, but two states have no usable one: a route before its
 * first Build, and a route whose last preview an edit has made stale. Both still have to show
 * pins, so the stop list stands in for the preview until a real one arrives.
 *
 * Returns undefined for a stop with no coordinates — it cannot be placed, so the caller skips it
 * rather than emitting a marker with no position.
 *
 * Each branch is checked with `satisfies` rather than asserted with `as`, so dropping a required
 * field later fails the build instead of compiling into an invalid stop. Every field a styler
 * reads is set explicitly; the metric fields come from the defaults above.
 */
const routeStopToPreviewStop = (
  stop: EditorRouteStop,
  displayOrder: number
): RoutePreviewStop | undefined => {
  if (stop.entity === EntityType.COMPANY) {
    return stop.account?.geoPoint
      ? ({
          ...PREVIEW_METRIC_DEFAULTS,
          accountId: stop.accountId,
          address: stop.account.address,
          displayOrder,
          entity: EntityType.COMPANY,
          geoPoint: stop.account.geoPoint,
          name: stop.account.name ?? "",
          scheduledTime: stop.scheduledTime,
        } satisfies RoutePreviewStop)
      : undefined;
  }
  if (stop.entity === EntityType.PERSON) {
    return stop.contact?.geoPoint
      ? ({
          ...PREVIEW_METRIC_DEFAULTS,
          address: stop.contact.address,
          contactId: stop.contactId,
          displayOrder,
          entity: EntityType.PERSON,
          geoPoint: stop.contact.geoPoint,
          name: stop.contact.name ?? "",
          scheduledTime: stop.scheduledTime,
        } satisfies RoutePreviewStop)
      : undefined;
  }
  return stop.manualGeoPoint
    ? ({
        ...PREVIEW_METRIC_DEFAULTS,
        displayOrder,
        entity: null,
        manualAddress: stop.manualAddress,
        manualGeoPoint: stop.manualGeoPoint,
        manualName: stop.manualName,
        scheduledTime: stop.scheduledTime,
      } satisfies RoutePreviewStop)
    : undefined;
};

export default routeStopToPreviewStop;
